import { AuthState } from '@/features/auth/types/auth-state';
import { User } from '@/features/user/types/user';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { v4 as uuid } from 'uuid';

function setLoggingUser(user: Sentry.User | null) {
  Sentry.setUser(user);
  if (window._paq) {
    if (user) {
      window._paq.push(['setUserId', user.id]);
      window._paq.push(['trackEvent', 'User', 'Login']);
    } else {
      window._paq.push(['resetUserId']);
      window._paq.push(['trackEvent', 'User', 'Logout']);
    }
  }

  if (window.ReactNativeWebView) {
    const id = uuid();
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        action: user ? 'SET_SENTRY_USER' : 'UNSET_SENTRY_USER',
        uuid: id,
        from: 'TIRECLOUD_RN_WEBVIEW',
        input: user
          ? {
              email: user?.email,
              username: user?.username,
              id: user?.id,
            }
          : {},
      })
    );
  }
}

export default createSlice({
  name: 'auth',
  initialState: {
    token: null,
    user: null,
    initialized: false,
    autoLogout: false,
    isAccessTokenStillValid: false,
    isRefreshTokenStillValid: false,
  } as AuthState & { initialized: boolean; autoLogout: boolean },
  reducers: {
    initializeAuthState(state) {
      setLoggingUser(null);

      return {
        ...state,
        token: null,
        user: null,
        initialized: true,
        autoLogout: false,
        isAccessTokenStillValid: false,
        isRefreshTokenStillValid: false,
      };
    },
    loginUser(
      _state,
      action: PayloadAction<Omit<AuthState, 'isAccessTokenStillValid' | 'isRefreshTokenStillValid'>>
    ) {
      if (action.payload.user) {
        setLoggingUser({
          id: action.payload.user.id,
          email:
            action.payload.user.contactInfo.email ||
            action.payload.user.contactInfo.phoneNumber ||
            action.payload.user.userNumber,
          username: action.payload.user.username,
        });
      }
      return {
        token: action.payload.token,
        user: action.payload.user,
        initialized: true,
        autoLogout: false,
        isAccessTokenStillValid: true,
        isRefreshTokenStillValid: true,
      };
    },
    logoutUser(
      state,
      action: PayloadAction<{
        autoLogout?: boolean;
      }>
    ) {
      setLoggingUser(null);
      return {
        ...state,
        token: null,
        user: null,
        initialized: true,
        autoLogout: action.payload.autoLogout || false,
        isAccessTokenStillValid: false,
        isRefreshTokenStillValid: false,
      };
    },
    updateUser(state, action: PayloadAction<User>) {
      if (action.payload) {
        setLoggingUser({
          id: action.payload.id,
          email:
            action.payload.contactInfo.email ||
            action.payload.contactInfo.phoneNumber ||
            action.payload.userNumber,
          username: action.payload.username,
        });
      }

      return {
        ...state,
        user: action.payload,
        isAccessTokenStillValid: true,
        isRefreshTokenStillValid: true,
      };
    },
    updateToken(state, action: PayloadAction<AuthState['token']>) {
      return {
        ...state,
        token: action.payload,
        isAccessTokenStillValid: true,
        isRefreshTokenStillValid: true,
      };
    },
    updateTokenValidity(
      state,
      action: PayloadAction<{
        isAccessTokenStillValid: boolean;
        isRefreshTokenStillValid: boolean;
      }>
    ) {
      return {
        ...state,
        isAccessTokenStillValid: action.payload.isAccessTokenStillValid,
        isRefreshTokenStillValid: action.payload.isRefreshTokenStillValid,
      };
    },
    updateAccessTokenValidity(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isAccessTokenStillValid: action.payload,
      };
    },
    updateRefreshTokenValidity(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isRefreshTokenStillValid: action.payload,
      };
    },
  },
});
